import React, { useEffect, useRef, useState } from 'react';
import {
  PanResponder,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { useTranslation } from 'react-i18next';

import type { StreamCamera } from '@/entities/stream/types';
import { fonts } from '@/shared/ui/fonts';
import { Icon, type IconName } from '@/shared/ui/icons';
import { AppText } from '@/shared/ui/text';
import { spacing, useTheme } from '@/shared/ui/theme';
import {
  DEFAULT_PTZ_SPEED,
  moveVector,
  readJoystick,
  usePtz,
  type JoyReading,
  type PtzDirection,
} from '../lib/ptz';

/**
 * Пульт PTZ — как в Hik-Connect / EZVIZ: джойстик с ручкой по центру, по бокам
 * круглые кнопки без подписи — слева «Зум −», справа «Зум +» (и «Домой» под
 * джойстиком, когда команды идут через облако).
 *
 * Джойстик: тянешь ручку — камера едет туда же, чем дальше от центра, тем быстрее
 * (1–7, цифра на ручке); отпустил — ручка в центр, камера встаёт. Нажать можно и
 * сразу у края — ручка прыгает под палец.
 *
 * Вид — ui-kit: «колодец» `sunken` с рамкой, ручка — поверхность `elevated` (без
 * тени: в ui-kit тень только у всплывающего), красное — только у того, что сейчас
 * ведёт камеру. В шапке — путь команд («напрямую» / «через облако»); шапка
 * сворачивает пульт до строки.
 */
const SECTORS: PtzDirection[] = [
  'up',
  'upRight',
  'right',
  'downRight',
  'down',
  'downLeft',
  'left',
  'upLeft',
];

/** Возврат ручки в центр — CSS-переходом; в приложении она просто встаёт на место. */
const GLIDE =
  Platform.OS === 'web'
    ? ({
        transitionProperty: 'left, top',
        transitionDuration: '160ms',
        transitionTimingFunction: 'cubic-bezier(.2,.9,.3,1.2)',
      } as object)
    : {};
const NO_GLIDE =
  Platform.OS === 'web' ? ({ transitionDuration: '0ms' } as object) : {};

/** Короткий отклик пальцу в момент, когда камера тронулась (Android-браузер). */
function buzz() {
  const nav = (globalThis as { navigator?: { vibrate?: (ms: number) => void } })
    .navigator;
  try {
    nav?.vibrate?.(8);
  } catch {
    // Браузер без вибрации или запретил её — молча.
  }
}

/** Shift + стрелки → сторона; две стрелки разом — диагональ. */
function keyDirection(keys: Set<string>): PtzDirection | null {
  const x = (keys.has('ArrowRight') ? 1 : 0) - (keys.has('ArrowLeft') ? 1 : 0);
  const y = (keys.has('ArrowUp') ? 1 : 0) - (keys.has('ArrowDown') ? 1 : 0);
  if (!x && !y) {
    return null;
  }
  const r = readJoystick(x, y);
  return r ? r.direction : null;
}

/** Долгое нажатие на «Зум +» не должно выделять подпись, как текст на странице. */
const NO_SELECT =
  Platform.OS === 'web' ? ({ userSelect: 'none' } as object) : {};

/** Без этого на телефоне палец на джойстике прокручивает страницу. */
const NO_SCROLL =
  Platform.OS === 'web' ? ({ touchAction: 'none' } as object) : {};

const styles = StyleSheet.create({
  root: { borderTopWidth: StyleSheet.hairlineWidth },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    height: 36,
    paddingHorizontal: spacing(3),
  },
  title: { flex: 1, minWidth: 0 },
  route: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  dot: { width: 6, height: 6, borderRadius: 3 },
  body: {
    alignItems: 'center',
    gap: spacing(2),
    paddingHorizontal: spacing(3),
    paddingTop: spacing(1),
    paddingBottom: spacing(4),
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing(4),
  },
  round: { alignItems: 'center', justifyContent: 'center', borderWidth: 1 },
  disc: { borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  ring: { position: 'absolute', borderWidth: 1 },
  mark: {
    position: 'absolute',
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tick: { width: 3, height: 3, borderRadius: 1.5 },
  knob: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  knobCore: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  speed: { fontFamily: fonts.mono.medium, fontSize: 13 },
  error: { paddingHorizontal: spacing(3), paddingBottom: spacing(3) },
});

type PressState = { pressed: boolean; hovered?: boolean };
type Hold = { onPressIn: () => void; onPressOut: () => void };

/**
 * Джойстик с ручкой. Жест — PanResponder: один и тот же для пальца и мыши, на вебе
 * и в приложении. Ручка рисуется там, где палец; камере уходит только смена стороны
 * или ступени скорости — не каждое движение пальца.
 */
function Joystick({
  size,
  moving,
  speed,
  onDrive,
  onRelease,
}: {
  size: number;
  moving: string | null;
  speed: number | null;
  onDrive: (r: JoyReading) => void;
  onRelease: () => void;
}) {
  const { t } = useTranslation();
  const p = useTheme();
  const knob = Math.round(size * 0.36);
  const reach = size / 2 - knob / 2 - 6;
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const origin = useRef({ x: 0, y: 0 });
  const last = useRef('');
  const handlers = useRef({ onDrive, onRelease });
  handlers.current = { onDrive, onRelease };

  const apply = (px: number, py: number) => {
    let dx = px;
    let dy = py;
    const d = Math.hypot(dx, dy);
    if (d > reach) {
      dx *= reach / d;
      dy *= reach / d;
    }
    setPos({ x: dx, y: dy });
    const r = readJoystick(dx / reach, -dy / reach);
    const sig = r ? `${r.direction}:${r.speed}` : '';
    if (sig !== last.current) {
      last.current = sig;
      if (r) {
        handlers.current.onDrive(r);
      } else {
        handlers.current.onRelease();
      }
    }
  };
  const applyRef = useRef(apply);
  applyRef.current = apply;

  const end = () => {
    last.current = '';
    setDragging(false);
    setPos({ x: 0, y: 0 });
    handlers.current.onRelease();
  };
  const endRef = useRef(end);
  endRef.current = end;
  const setDraggingRef = useRef(setDragging);

  const responder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      // Палец на джойстике — не прокрутка страницы, даже если он уехал за край.
      onPanResponderTerminationRequest: () => false,
      onPanResponderGrant: e => {
        setDraggingRef.current(true);
        origin.current = {
          x: e.nativeEvent.locationX - size / 2,
          y: e.nativeEvent.locationY - size / 2,
        };
        applyRef.current(origin.current.x, origin.current.y);
      },
      onPanResponderMove: (_, g) =>
        applyRef.current(origin.current.x + g.dx, origin.current.y + g.dy),
      onPanResponderRelease: () => endRef.current(),
      onPanResponderTerminate: () => endRef.current(),
    }),
  ).current;

  const active = moving !== null && SECTORS.includes(moving as PtzDirection);
  const markR = size / 2 - 13;
  const core = Math.round(knob * 0.62);
  return (
    <View
      {...responder.panHandlers}
      accessibilityRole="adjustable"
      accessibilityLabel={t('live.ptz.joystick')}
      {...({ title: t('live.ptz.joystickHint') } as object)}
      testID="live-ptz-pad"
      style={[
        styles.disc,
        NO_SCROLL,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: p.sunken,
          borderColor: active ? p.borderStrong : p.border,
        },
      ]}
    >
      {/* Дорожка ручки: за ней скорость уже полная. */}
      <View
        pointerEvents="none"
        style={[
          styles.ring,
          {
            width: size - 30,
            height: size - 30,
            borderRadius: (size - 30) / 2,
            borderColor: p.borderSubtle,
          },
        ]}
      />
      {SECTORS.map((dir, i) => {
        const angle = (i * Math.PI) / 4;
        const lit = active && moving === dir;
        return (
          <View
            key={dir}
            pointerEvents="none"
            style={[
              styles.mark,
              {
                left: size / 2 + markR * Math.sin(angle) - 10 - 1,
                top: size / 2 - markR * Math.cos(angle) - 10 - 1,
              },
            ]}
          >
            {i % 2 ? (
              <View
                style={[
                  styles.tick,
                  { backgroundColor: lit ? p.primary : p.borderStrong },
                ]}
              />
            ) : (
              <View style={{ transform: [{ rotate: `${i * 45}deg` }] }}>
                <Icon
                  name="chevronUp"
                  size={15}
                  color={lit ? p.primary : p.textSecondary}
                />
              </View>
            )}
          </View>
        );
      })}
      <View
        pointerEvents="none"
        style={[
          styles.knob,
          {
            width: knob,
            height: knob,
            borderRadius: knob / 2,
            left: size / 2 - knob / 2 - 1 + pos.x,
            top: size / 2 - knob / 2 - 1 + pos.y,
            // Под пальцем — без задержки; отпустили — ручка плавно уходит в центр.
            ...(dragging ? NO_GLIDE : GLIDE),
            backgroundColor: p.elevated,
            borderColor: active ? p.primary : p.borderStrong,
          },
        ]}
      >
        <View
          style={[
            styles.knobCore,
            {
              width: core,
              height: core,
              borderRadius: core / 2,
              borderColor: active ? p.primarySoft : p.border,
              backgroundColor: active ? p.primarySoft : p.card,
            },
          ]}
        >
          {active && speed ? (
            <AppText color={p.primaryInk} style={styles.speed}>
              {speed}
            </AppText>
          ) : null}
        </View>
      </View>
    </View>
  );
}

/**
 * Круглая кнопка без подписи по бокам джойстика: слева «Зум −», справа «Зум +».
 * Что она делает — в `accessibilityLabel` и всплывающей подсказке. Держишь —
 * действует (зум), щелчок — разово («Домой»).
 */
function RoundKey({
  icon,
  label,
  size,
  active = false,
  testID,
  ...press
}: {
  icon: IconName;
  label: string;
  size: number;
  active?: boolean;
  testID: string;
  onPressIn?: () => void;
  onPressOut?: () => void;
  onPress?: () => void;
}) {
  const p = useTheme();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      {...({ title: label } as object)}
      testID={testID}
      {...press}
      style={state => [
        styles.round,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          borderColor: active ? p.primary : p.borderStrong,
          backgroundColor: active
            ? p.primarySoft
            : state.pressed || (state as PressState).hovered
            ? p.accent
            : p.sunken,
        },
      ]}
    >
      <Icon
        name={icon}
        size={Math.round(size * 0.45)}
        color={active ? p.primary : p.textSecondary}
      />
    </Pressable>
  );
}

export function PtzPanel({
  companyId,
  camera,
  large = false,
  collapsed = false,
  onToggle,
}: {
  companyId: string;
  camera: StreamCamera;
  /** Телефон: крупнее, под палец. */
  large?: boolean;
  /** Свёрнут до шапки. */
  collapsed?: boolean;
  onToggle?: () => void;
}) {
  const { t } = useTranslation();
  const p = useTheme();
  const ptz = usePtz(companyId, camera);
  const [speed, setSpeed] = useState<number | null>(null);
  const disc = large ? 156 : 128;
  // 34 на столе, 44 под палец — высоты кнопок ui-kit.
  const keySize = large ? 44 : 34;

  const drive = (r: JoyReading) => {
    if (speed === null) {
      buzz();
    }
    setSpeed(r.speed);
    ptz.start(r.direction, moveVector(r.direction, 0, r.speed));
  };
  const release = () => {
    setSpeed(null);
    ptz.stop();
  };
  // Клавиатура, как в настольных VMS: Shift + стрелки — поворот (две разом —
  // диагональ), Shift + «+»/«−» — зум. Без Shift стрелки по-прежнему выбирают окно.
  const ptzRef = useRef(ptz);
  ptzRef.current = ptz;
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }
    const held = new Set<string>();
    let zoomKey: 'zoomIn' | 'zoomOut' | null = null;
    const typing = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      return (
        !!el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.isContentEditable)
      );
    };
    const apply = () => {
      const ctl = ptzRef.current;
      if (zoomKey) {
        ctl.start(
          zoomKey,
          moveVector(null, zoomKey === 'zoomIn' ? 1 : -1, DEFAULT_PTZ_SPEED),
        );
        return;
      }
      const dir = keyDirection(held);
      if (dir) {
        ctl.start(dir, moveVector(dir, 0, DEFAULT_PTZ_SPEED));
        setSpeed(DEFAULT_PTZ_SPEED);
      } else {
        ctl.stop();
        setSpeed(null);
      }
    };
    const down = (e: KeyboardEvent) => {
      if (!e.shiftKey || typing(e)) {
        return;
      }
      if (e.key.startsWith('Arrow')) {
        e.preventDefault();
        if (!e.repeat) {
          held.add(e.key);
          apply();
        }
      } else if ('+='.includes(e.key) || '-_'.includes(e.key)) {
        e.preventDefault();
        if (!e.repeat) {
          zoomKey = '+='.includes(e.key) ? 'zoomIn' : 'zoomOut';
          apply();
        }
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        held.clear();
        zoomKey = null;
        apply();
      } else if (held.delete(e.key)) {
        apply();
      } else if (zoomKey && '+=-_'.includes(e.key)) {
        zoomKey = null;
        apply();
      }
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  const zoom = (k: 'zoomIn' | 'zoomOut'): Hold => ({
    onPressIn: () =>
      ptz.start(
        k,
        moveVector(null, k === 'zoomIn' ? 1 : -1, DEFAULT_PTZ_SPEED),
      ),
    onPressOut: ptz.stop,
  });

  return (
    <View
      style={[styles.root, NO_SELECT, { borderTopColor: p.border }]}
      testID="live-ptz"
    >
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: !collapsed }}
        accessibilityLabel={t(
          collapsed ? 'live.ptz.expand' : 'live.ptz.collapse',
        )}
        onPress={onToggle}
        testID="live-ptz-header"
        style={state => [
          styles.header,
          (state as PressState).hovered && { backgroundColor: p.accent },
        ]}
      >
        <Icon name="ptz" size={14} color={p.textSecondary} />
        <AppText
          variant="label"
          numberOfLines={1}
          color={p.foreground}
          style={styles.title}
        >
          {`${t('live.ptz.title')} · ${camera.name || camera.deviceId}`}
        </AppText>
        <View
          style={styles.route}
          {...({
            title: t(ptz.direct ? 'live.ptz.directHint' : 'live.ptz.cloudHint'),
          } as object)}
          testID="live-ptz-route"
        >
          <View
            style={[
              styles.dot,
              { backgroundColor: ptz.direct ? p.signal : p.warning },
            ]}
          />
          <AppText variant="micro" color={p.mutedForeground}>
            {t(ptz.direct ? 'live.ptz.direct' : 'live.ptz.cloud')}
          </AppText>
        </View>
        <Icon
          name={collapsed ? 'chevronDown' : 'chevronUp'}
          size={14}
          color={p.mutedForeground}
        />
      </Pressable>

      {collapsed ? null : (
        <View style={styles.body}>
          <View style={styles.row}>
            <RoundKey
              icon="zoomOut"
              label={t('live.ptz.zoomOut')}
              size={keySize}
              active={ptz.moving === 'zoomOut'}
              testID="live-ptz-zoom-out"
              {...zoom('zoomOut')}
            />
            <Joystick
              size={disc}
              moving={ptz.moving}
              speed={speed}
              onDrive={drive}
              onRelease={release}
            />
            <RoundKey
              icon="zoomIn"
              label={t('live.ptz.zoomIn')}
              size={keySize}
              active={ptz.moving === 'zoomIn'}
              testID="live-ptz-zoom-in"
              {...zoom('zoomIn')}
            />
          </View>
          {/* «Домой» по прямому каналу регистратор не принимает — тогда кнопки нет
              вовсе, а не серая, про которую на телефоне не узнать, почему она не
              нажимается. */}
          {ptz.direct ? null : (
            <RoundKey
              icon="home"
              label={t('live.ptz.home')}
              size={keySize}
              testID="live-ptz-home"
              onPress={ptz.home}
            />
          )}
        </View>
      )}

      {!collapsed && ptz.error ? (
        <View style={styles.error}>
          <AppText variant="micro" color={p.warningInk}>
            {t(`live.ptz.error.${ptz.error}`)}
          </AppText>
        </View>
      ) : null}
    </View>
  );
}
